import React from 'react';
import { Card, CardContent } from '../Card';
import { Link } from 'react-router-dom';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../Table';
import { Badge } from '../Badge';
import { TrendingUp, TrendingDown, Minus, Radar, ArrowUpRight } from 'lucide-react';
type Trend = 'up' | 'down' | 'flat';
type Status = 'ORGANIK' | 'TERKOORDINASI';
const issues: Array<{
  id: number;
  name: string;
  vol: string;
  pos: number;
  neu: number;
  neg: number;
  region: string;
  tag: string;
  status: Status;
  trend: Trend;
}> = [{
  id: 1,
  name: 'Harga beras melambung',
  vol: '124K',
  pos: 10,
  neu: 20,
  neg: 70,
  region: 'Jawa, Sumatera',
  tag: 'EKONOMI',
  status: 'ORGANIK',
  trend: 'up'
}, {
  id: 2,
  name: 'Hoaks bansos pangan',
  vol: '89K',
  pos: 5,
  neu: 15,
  neg: 80,
  region: 'Nasional',
  tag: 'SOSIAL',
  status: 'TERKOORDINASI',
  trend: 'up'
}, {
  id: 3,
  name: 'Deepfake audio Presiden',
  vol: '72K',
  pos: 2,
  neu: 8,
  neg: 90,
  region: 'Nasional',
  tag: 'KEAMANAN',
  status: 'TERKOORDINASI',
  trend: 'up'
}, {
  id: 4,
  name: 'IKN tahap 2',
  vol: '65K',
  pos: 45,
  neu: 30,
  neg: 25,
  region: 'Kalimantan, Jakarta',
  tag: 'POLITIK',
  status: 'ORGANIK',
  trend: 'down'
}, {
  id: 5,
  name: 'Demo buruh upah minimum',
  vol: '52K',
  pos: 15,
  neu: 25,
  neg: 60,
  region: 'Jabar, Banten',
  tag: 'EKONOMI',
  status: 'TERKOORDINASI',
  trend: 'up'
}, {
  id: 6,
  name: 'Hilirisasi nikel',
  vol: '48K',
  pos: 55,
  neu: 25,
  neg: 20,
  region: 'Sulawesi',
  tag: 'EKONOMI',
  status: 'ORGANIK',
  trend: 'flat'
}, {
  id: 7,
  name: 'BBM subsidi tepat sasaran',
  vol: '41K',
  pos: 30,
  neu: 40,
  neg: 30,
  region: 'Nasional',
  tag: 'EKONOMI',
  status: 'ORGANIK',
  trend: 'down'
}, {
  id: 8,
  name: 'Banjir Demak',
  vol: '38K',
  pos: 10,
  neu: 30,
  neg: 60,
  region: 'Jawa Tengah',
  tag: 'SOSIAL',
  status: 'ORGANIK',
  trend: 'up'
}];
const tagTone: Record<string, string> = {
  EKONOMI: 'bg-amber-50 text-amber-700 border-amber-200',
  SOSIAL: 'bg-blue-50 text-blue-700 border-blue-200',
  POLITIK: 'bg-purple-50 text-purple-700 border-purple-200',
  KEAMANAN: 'bg-red-50 text-red-700 border-red-200'
};
function TrendIcon({
  t
}: {
  t: Trend;
}) {
  if (t === 'up') return <TrendingUp className="size-3.5 text-red-600" data-id="element-477" />;
  if (t === 'down') return <TrendingDown className="size-3.5 text-emerald-600" data-id="element-478" />;
  return <Minus className="size-3.5 text-muted-foreground" data-id="element-479" />;
}
export function IssueRadar() {
  return <Card className="bg-card border-border/60 h-full flex flex-col" data-id="element-480">
      <CardContent className="p-0 flex flex-col h-full" data-id="element-481">
        {/* Header */}
        <div className="flex items-center justify-between px-4 sm:px-5 py-3.5 border-b border-border/60" data-id="element-482">
          <div className="flex items-center gap-2.5" data-id="element-483">
            <div className="size-7 rounded-md bg-primary/10 border border-primary/20 flex items-center justify-center" data-id="element-484">
              <Radar className="size-3.5 text-primary" data-id="element-485" />
            </div>
            <div data-id="element-486">
              <h3 className="font-heading text-sm font-bold tracking-tight leading-tight" data-id="element-487">
                Top Isu Trending 24 Jam
              </h3>
              <p className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground mt-0.5" data-id="element-488">
                8 isu teratas · disusun berdasar volume percakapan
              </p>
            </div>
          </div>
          <Link to="/hot-issue" className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-wider text-primary hover:underline font-bold" data-id="element-489">
            Buka Hot Issue
            <ArrowUpRight className="size-3" data-id="element-490" />
          </Link>
        </div>

        {/* Table */}
        <div className="flex-1 min-h-0 overflow-y-auto scrollbar-thin scrollbar-thumb-border scrollbar-track-transparent" data-id="element-491">
          <Table data-id="element-492">
            <TableHeader className="bg-muted/30 sticky top-0 z-10" data-id="element-493">
              <TableRow className="border-border/60 hover:bg-transparent" data-id="element-494">
                <TableHead className="font-mono text-[9px] uppercase tracking-wider w-8" data-id="element-495">
                  #
                </TableHead>
                <TableHead className="font-mono text-[9px] uppercase tracking-wider" data-id="element-496">
                  Isu
                </TableHead>
                <TableHead className="font-mono text-[9px] uppercase tracking-wider w-24" data-id="element-497">
                  Volume
                </TableHead>
                <TableHead className="font-mono text-[9px] uppercase tracking-wider w-[180px]" data-id="element-498">
                  Sentimen
                </TableHead>
                <TableHead className="font-mono text-[9px] uppercase tracking-wider w-32" data-id="element-499">
                  Wilayah
                </TableHead>
                <TableHead className="font-mono text-[9px] uppercase tracking-wider w-24" data-id="element-500">
                  Status
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody data-id="element-501">
              {issues.map((i, idx) => <TableRow key={i.id} className="border-border/40 hover:bg-muted/30 cursor-pointer" data-id="element-502">
                  <TableCell className="font-mono text-[10px] text-muted-foreground" data-id="element-503">
                    {String(idx + 1).padStart(2, '0')}
                  </TableCell>
                  <TableCell data-id="element-504">
                    <Link to="/hot-issue" className="block" data-id="element-505">
                      <div className="flex items-center gap-1.5" data-id="element-506">
                        <span className="text-xs font-medium truncate hover:text-primary transition" data-id="element-507">
                          {i.name}
                        </span>
                        <Badge variant="outline" className={`font-mono text-[8px] h-4 px-1 ${tagTone[i.tag] ?? ''}`} data-id="element-508">
                          {i.tag}
                        </Badge>
                      </div>
                    </Link>
                  </TableCell>
                  <TableCell data-id="element-509">
                    <div className="flex items-center gap-1.5" data-id="element-510">
                      <span className="font-heading text-xs font-bold" data-id="element-511">
                        {i.vol}
                      </span>
                      <TrendIcon t={i.trend} data-id="element-512" />
                    </div>
                  </TableCell>
                  <TableCell data-id="element-513">
                    <div className="w-full h-1.5 rounded-full bg-muted overflow-hidden flex" data-id="element-514">
                      <div className="h-full bg-emerald-500" style={{
                    width: `${i.pos}%`
                  }} data-id="element-515" />
                      <div className="h-full bg-slate-400" style={{
                    width: `${i.neu}%`
                  }} data-id="element-516" />
                      <div className="h-full bg-red-500" style={{
                    width: `${i.neg}%`
                  }} data-id="element-517" />
                    </div>
                    <div className="flex items-center gap-2 mt-1 font-mono text-[9px] text-muted-foreground" data-id="element-518">
                      <span className="text-emerald-700" data-id="element-519">+{i.pos}</span>
                      <span data-id="element-520">·</span>
                      <span data-id="element-521">{i.neu}</span>
                      <span data-id="element-522">·</span>
                      <span className="text-red-700" data-id="element-523">−{i.neg}</span>
                    </div>
                  </TableCell>
                  <TableCell className="text-[11px] text-muted-foreground truncate" data-id="element-524">
                    {i.region}
                  </TableCell>
                  <TableCell data-id="element-525">
                    <Badge variant="outline" className={`font-mono text-[8px] h-4 px-1.5 ${i.status === 'TERKOORDINASI' ? 'bg-red-50 text-red-700 border-red-200' : 'bg-emerald-50 text-emerald-700 border-emerald-200'}`} data-id="element-526">
                      {i.status}
                    </Badge>
                  </TableCell>
                </TableRow>)}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>;
}